// @ts-strict-ignore
import type { PropsWithChildren, ReactNode } from 'react';
import { Link } from 'react-router';
import { Stack, styled } from '@mui/material';
import { tss } from 'tss-react/mui';
import { FormattedMessage } from 'react-intl';
import { getThemeColor } from '@/muiTheme';
import { ArrowRightIcon } from '../icons';
import { H4 } from '../H4';
import { Paragraph } from '../Paragraph';
import Text from '../Text';

export function EditInfoListLayout({
    editLinkTo,
    children,
    disabledEditLink = false,
    title,
    description,
}: PropsWithChildren<{
    editLinkTo: string;
    disabledEditLink?: boolean;
    title?: ReactNode;
    description?: ReactNode;
}>) {
    const { classes } = useEditInfoListLayoutStyles();

    return (
        <Stack spacing={1}>
            {title && <H4 className={classes.title}>{title}</H4>}
            {description && (
                <Paragraph color="gray" className={classes.description}>
                    {description}
                </Paragraph>
            )}
            <div className={classes.editInfoListLayout}>
                <div className={classes.editInfoListLayoutContent}>
                    <dl className={classes.InfoList}>{children}</dl>
                    {!disabledEditLink && (
                        <EditLink className={classes.editLink} to={editLinkTo}>
                            <span className={classes.editLinkText}>
                                <FormattedMessage
                                    defaultMessage="Edit"
                                    description="Edit link for team settings components"
                                />
                            </span>
                            <i className={classes.editLinkIcon}>
                                <ArrowRightIcon size={14} />
                            </i>
                        </EditLink>
                    )}
                </div>
            </div>
        </Stack>
    );
}

const useEditInfoListLayoutStyles = tss.create(({ theme }) => ({
    title: {
        marginBottom: 0,
    },
    description: {
        marginBottom: 0,
    },
    editInfoListLayout: {
        padding: '16px 16px 24px',
        border: `1px solid ${getThemeColor(theme)('lightGray')}`,
        borderRadius: 6,
    },
    editInfoListLayoutContent: {
        position: 'relative',
    },
    InfoList: {
        flex: 1,
        margin: 0,
        '& dt': {
            margin: 0,
            marginTop: theme.spacing(1),
        },
        '& dd': {
            margin: 0,
        },
    },
    editLink: {
        position: 'absolute',
        right: 0,
        top: 0,
    },
    editLinkText: {
        marginRight: 8,
    },
    editLinkIcon: {
        verticalAlign: 'middle',
    },
}));

const EditLink = styled(Link)(() => ({
    fontSize: 12,
    textTransform: 'uppercase',
    fontWeight: 'bold',
}));

export function InfoGroup({
    label,
    value,
}: {
    label: ReactNode;
    value: ReactNode | ReactNode[];
}) {
    const { classes, cx } = useInfoGroupStyles();
    const infoValueClassNames = [classes.infoValue];

    const descriptionElement = Array.isArray(value) ? (
        value.length > 0 ? (
            value.filter(Boolean).map((valItem, idx) => (
                <dd
                    key={idx}
                    className={cx(
                        classes.infoValue__list,
                        ...infoValueClassNames,
                    )}
                >
                    <Text
                        sx={(theme) => ({ color: theme.palette.text.primary })}
                    >
                        {valItem}
                    </Text>
                </dd>
            ))
        ) : (
            '-'
        )
    ) : (
        <dd className={cx(...infoValueClassNames)}>
            <Text
                className={classes.infoValueText}
                sx={(theme) => ({ color: theme.palette.text.primary })}
            >
                {value ? value : '-'}
            </Text>
        </dd>
    );

    return (
        <>
            <dt>
                <Text
                    size="sm"
                    textTransform="uppercase"
                    sx={(theme) => ({ color: theme.palette.text.secondary })}
                >
                    {label}
                </Text>
            </dt>
            {descriptionElement}
        </>
    );
}

const useInfoGroupStyles = tss.create(({ theme }) => ({
    infoValue: {
        marginLeft: 0,
    },
    infoValue__list: {
        display: 'flex',
        alignItems: 'center',
        '&::before': {
            content: "' '",
            display: 'inline-block',
            width: 6,
            height: 6,
            backgroundColor: getThemeColor(theme)('black'),
            borderRadius: '50%',
            marginRight: 8,
        },
    },
    infoValueText: {
        display: 'block',
    },
}));
